import { LOGO_CORREO } from '@/libs/emailAssets'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import store from '@/store'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import endpoints from '@/libs/endpoints'

export default function useCommonTodo() {

  const userData = store.state?.userStore?.userData;
  const userId = userData.worker_id != null ? userData.worker_id.$oid : null
  const { default_language, commitment_functionality: commitmentFunctionality } = JSON.parse(localStorage.getItem('clientData') || '{}')
  const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { createItem, updateItem, updateItems, getItem, getItems, getItemsAggregate, sendEmail, getPresignedUploadUrl } = useLambda()
  const now = new Date()
  now.setHours(12, 0, 0, 0)
  const tagColors = {
    people: "success",
    place: "info",
    processes: "warning",
    customer_need: "danger",
    tools_team: "info",
    positive_client_voice: "success",
    negative_client_voice: "danger",
    safety: "success",
    processes_productivity: "info",
    assets_management: "warning",
    quality: "danger",
    sustainability: "secondary",
    costs: "dark",
    rdp: "success",
  }

  const resolveTagVariant = tag => {
    return tagColors[tag] || "success"
  }
  
  const resolveAvatarVariant = tags => {
    if (!tags || !tags.length) return 'secondary'
    for (const tag of tags) {
      if (tagColors.hasOwnProperty(tag)) return tagColors[tag]
    }
    return 'success'
  }

  const updateImprovementWithKey = async (improvementId, key) => {
    try {
      const query = { _id: oid(improvementId) }
      const action = { $set: { imageKey: key } }

      await updateItem({ collection: 'improvement', query, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.improvement_update_error'))
    }
  }

  const getEmailImprovementTemplate = bodyData => {
    const { name, title, assignee, tags, creator, dueDate, description, creation_date, instance, instance_leader, zone, commitmentFunctionality, fromProblemSolving, updatedState, completed, deleted, category, id } = bodyData
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding: 0">
              <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="Habit Logo">
            </td>
          </tr>
          
          <tr>
            <td style="background-color: #ecf0f1">
              <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                <h2 style="color: #e67e22; margin: 0 0 7px">
                  ${i18n.t('message.hello')}${name ? ` ${name}` : ''},
                </h2>

                <p style="margin: 2px; font-size: 15px">
                  ${commitmentFunctionality
                    ? updatedState === "delete"
                      ? category === "assignee" ? i18n.t('message.commitment_assigned_deleted') : i18n.t('message.commitment_subscribed_deleted')
                      : updatedState === "complete"
                        ? category === "assignee" ? i18n.t('message.commitment_assigned_completed') : i18n.t('message.commitment_subscribed_completed')
                        : updatedState === "update"
                          ? category === "assignee" ? i18n.t('message.commitment_assigned_updated') : i18n.t('message.commitment_subscribed_updated')
                          : category === "assignee" ? i18n.t('message.assigned_responsible_commitment') : i18n.t('message.assigned_subscribed_commitment')
                    : updatedState === "delete"
                      ? category === "assignee" ? i18n.t('message.improvement_assigned_deleted') : i18n.t('message.improvement_subscribed_deleted')
                      : updatedState === "complete"
                        ? category === "assignee" ? i18n.t('message.improvement_assigned_completed') : i18n.t('message.improvement_subscribed_completed')
                        : updatedState === "update"
                          ? category === "assignee" ? i18n.t('message.improvement_assigned_updated') : i18n.t('message.improvement_subscribed_updated')
                          : category === "assignee" ? i18n.t('message.assigned_responsible_improvement') : i18n.t('message.assigned_subscriber_improvement')
                  }:
                </p>

                <ul style="font-size: 15px;  margin: 10px 0">
                  ${title ? `<li><u>${fromProblemSolving ? i18n.t('label.action') : i18n.t('label.Title')}</u>: <b>${title}</b></li>` : ''}
                  ${assignee ? `<li><u>${i18n.t('label.responsible')}</u>: <b>${assignee}</b></li>` : ''}
                  ${tags ? `<li><u>Tags</u>: <b>${tags}</b></li>` : ''}
                  ${creator ? `<li><u>${i18n.t('improvement_opp.created_by')}</u>: <b>${creator}</b></li>` : ''}
                  ${dueDate ? `<li><u>${i18n.t('label.due_date')}</u>: <b>${dueDate}</b></li>` : ''}
                  ${description ? `<li><u>${fromProblemSolving ? i18n.t('label.root_cause') : i18n.t('label.description')}</u>: <b>${description}</b></li>` : ''}
                  ${creation_date ? `<li><u>${i18n.t('metadata.creation_date')}</u>: <b>${creation_date}</b></li>` : ''}
                  ${instance ? `<li><u>${i18n.t('metadata.instance')}</u>: <b>${instance}</b></li>` : ''}
                  ${instance_leader ? `<li><u>${i18n.t('metadata.instance_leader')}</u>: <b>${instance_leader}</b></li>` : ''}
                  ${zone ? `<li><u>${i18n.t('metadata.zone')}</u>: <b>${zone}</b></li>` : ''}
                  ${`<li><u>${i18n.t('improvement_opp.state')}</u>: <b>${deleted ? i18n.t('deleted_singular') : completed ? i18n.t('completed') : i18n.t('pending')}</b></li>`}
                </ul>

                <div style="width: 100%; text-align: center; margin: 7% 0%;">
                  <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #4e8769; font-size: 17px" href="${endpoints.app_url}/apps/todo${`?originId=${id}`}">
                    ${commitmentFunctionality ? i18n.t('message.see_commitment_in_habit') : i18n.t('message.see_improvement_in_habit')}
                  </a>
                </div>
              </div>
            </td>
          </tr>
        </table>
      </html>
    `
  }

  const getErrorTemplate = bodyData => {
    const { user, error, errorString, errorMessage, payload, dataFromPayload, origin } = bodyData
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding: 0">
              <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="Habit Logo">
            </td>
          </tr>
          
          <tr>
            <td style="background-color: #ecf0f1">
              <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                <p style="margin: 2px; font-size: 15px">
                  Se obtuvo el siguiente error al crear un diálogo diario:
                </p>

                <ul style="font-size: 15px;  margin: 10px 0">
                  ${user ? `<li><u>Usuario</u>: <b>${user}</b></li>` : ''}
                  ${error ? `<li><u>Error</u>: <b>${error}</b></li>` : ''}
                  ${errorString ? `<li><u>Error string</u>: <b>${errorString}</b></li>` : ''}
                  ${errorMessage ? `<li><u>Message</u>: <b>${errorMessage}</b></li>` : ''}
                  ${origin ? `<li><u>Origin</u>: <b>${origin}</b></li>` : ''}
                  ${payload ? `<li><u>Payload</u>: <b>${payload}</b></li>` : ''}
                  ${dataFromPayload ? `<li><u>Data From Payload</u>: <b>${dataFromPayload}</b></li>` : ''}
                </ul>
              </div>
            </td>
          </tr>
        </table>
      </html>
    `
  }

    const getEmailCommitmentTemplate = bodyData => {
      const { name, title, assignee, creator, dueDate, description, creationDate, fromProblemSolving, updatedState, completed, deleted, category, id } = bodyData
      return `
        <html>
          <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
            <tr>
              <td style="background-color: #ecf0f1; text-align: center; padding: 0">
                <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="Habit Logo">
              </td>
            </tr>
            
            <tr>
              <td style="background-color: #ecf0f1">
                <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                  <h2 style="color: #e67e22; margin: 0 0 7px">
                    ${i18n.t('message.hello')}${name ? ` ${name}` : ''},
                  </h2>
  
                  <p style="margin: 2px; font-size: 15px">
                    ${updatedState === "delete"
                      ? category === "assignee" ? i18n.t('message.commitment_assigned_deleted') : i18n.t('message.commitment_subscribed_deleted')
                      : updatedState === "complete"
                        ? category === "assignee" ? i18n.t('message.commitment_assigned_completed') : i18n.t('message.commitment_subscribed_completed')
                        : updatedState === "update"
                          ? category === "assignee" ? i18n.t('message.commitment_assigned_updated') : i18n.t('message.commitment_subscribed_updated')
                          : category === "assignee" ? i18n.t('message.assigned_responsible_commitment') : i18n.t('message.assigned_subscribed_commitment')
                    }:
                  </p>
  
                  <ul style="font-size: 15px;  margin: 10px 0">
                    ${title ? `<li><u>${fromProblemSolving ? i18n.t('label.action') : i18n.t('label.Title')}</u>: <b>${title}</b></li>` : ''}
                    ${assignee ? `<li><u>${i18n.t('label.responsible')}</u>: <b>${assignee}</b></li>` : ''}
                    ${creator ? `<li><u>${i18n.t('improvement_opp.created_by')}</u>: <b>${creator}</b></li>` : ''}
                    ${dueDate ? `<li><u>${i18n.t('label.due_date')}</u>: <b>${dueDate}</b></li>` : ''}
                    ${description ? `<li><u>${fromProblemSolving ? i18n.t('label.root_cause') : i18n.t('label.description')}</u>: <b>${description}</b></li>` : ''}
                    ${creationDate ? `<li><u>${i18n.t('metadata.creation_date')}</u>: <b>${creationDate}</b></li>` : ''}
                    ${`<li><u>${i18n.t('improvement_opp.state')}</u>: <b>${deleted ? i18n.t('deleted_singular') : completed ? i18n.t('completed') : i18n.t('pending')}</b></li>`}
                  </ul>
  
                  <div style="width: 100%; text-align: center; margin: 7% 0%;">
                    <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #4e8769; font-size: 17px" href="${endpoints.app_url}/habit/commitment/list?originId=${id}">
                      ${i18n.t('message.see_commitment_in_habit')}
                    </a>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </html>
      `
    }

  const addTask = async val => {
    try {
      const payload = {
        ...val,
        client_id: oid(val.client_id),
        assignee: val.assignee ? oid(val.assignee._id) : null,
        dueDate: val.dueDate ? date(new Date(`${val.dueDate.slice(0, 10)} 12:00:00`)) : null,
        subscribers: val.subscribers?.map(e => oid(e)) || [],
        metadata: val.metadata?.map(m => ({
          name: m.name,
          type: m.type,
          answer: m.answer,
        })) || [],
        origin: 'direct',
        created_by: oid(userId),
        creation_date: date(new Date())
      }

      delete payload.imgData

      if (payload.commitments?.length) {
        try {
          const newCommitmentsIds = await createCommitments(payload.commitments)
          if (newCommitmentsIds?.length) {
            payload.commitments = newCommitmentsIds.map(e => oid(e))
          }
        } catch (error) {
          console.log(error)
        }
      }

      const { insertedId } = await createItem({ collection: 'improvement', payload })
      if (!insertedId) throw new Error('Item not created')
      
      showSuccessMessage(commitmentFunctionality ? i18n.t('message.commitment_created') : i18n.t('message.improvement_created'))
      
      const improvementId = insertedId.toString()
      
      // Upload image to AWS and then update the confirmation in MongoDB with the AWS image key
      if (val.imgData) {
        const { fileInfo, destinationFolder } = val.imgData || {}
        uploadFileToS3(fileInfo, destinationFolder)
          .then((key) => updateImprovementWithKey(improvementId, key))
          .catch((err) => console.log(err))
      }

      // Send email to assignee and subscribers with the details of the improvement opportunity created
      val._id = improvementId
      sendEmailImprovementsOrCommitments([val], 'add')

      // Update commitments with improvement _id
      if (payload.commitments?.length) {
        const query = { _id: { $in: payload.commitments } }
        const action = { $set: { improvement: oid(insertedId) } }
        await updateItems({ collection: 'commitment', query, action })
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(commitmentFunctionality ? i18n.t('message.commitment_error') : i18n.t('message.improvement_error'))
    }
  }

  const getEmailIntanceTemplate = bodyData => {
    const { name, start, participants, instance, instance_leader, commitment, creator, state, id} = bodyData
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding: 0">
              <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="Habit Logo">
            </td>
          </tr>
          
          <tr>
            <td style="background-color: #ecf0f1">
              <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                <h2 style="color: #e67e22; margin: 0 0 7px">
                  ${i18n.t('message.hello')}${name ? ` ${name}` : ''},
                </h2>

                <p style="margin: 2px; font-size: 15px">
                  ${state === 'add' ? i18n.t('message.assigned_participant_instance')
                  : state === 'update' ? i18n.t('message.instance_assigned_updated')
                  : i18n.t('message.instance_assigned_deleted')
                  }
                </p>

                <ul style="font-size: 15px;  margin: 10px 0">                                                   
                  ${instance ? `<li><u>${i18n.t('metadata.instance')}</u>: <b>${instance}</b></li>` : ''}
                  ${instance_leader ? `<li><u>${i18n.t('metadata.instance_leader')}</u>: <b>${instance_leader}</b></li>` : ''}
                  ${participants ? `<li><u>${i18n.t('label.participants')}</u>: <b>${participants}</b></li>` : ''}
                  ${start ? `<li><u>${i18n.t('label.start_date')}</u>: <b>${start}</b></li>` : ''}
                  ${creator ? `<li><u>${i18n.t('improvement_opp.created_by')}</u>: <b>${creator}</b></li>` : ''}
                  ${commitment && commitment !== 0 ? `<li><u>${i18n.t('label.num_commitment_instance')}</u>: <b>${commitment}</b></li>` : '' }
                </ul>

                <div style="width: 100%; text-align: center; margin: 7% 0%;">
                  <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #4e8769; font-size: 17px" href="${endpoints.app_url}/apps/calendar?originId=${id}">
                    ${i18n.t('message.see_instance_in_habit')}
                  </a>
                </div>
              </div>
            </td>
          </tr>
        </table>
      </html>
    `
  }

  // Demo user message template for Habit commercial
  const getEmailDemoHabitTemplate = userData => {
    const { name, email, phone, company, message, meetUs, topic } = userData
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding-top: 2rem;">
              <img width="20%"" src="${LOGO_CORREO}" alt="Habit Logo">
            </td>
          </tr>
          
          <tr>
            <td style="background-color: #ecf0f1; padding: 1rem">
              <div style="color: #34495e; margin: 2rem; font-family: sans-serif">
                <h3 style="color: #56616d; margin: 0 0 7px; margin-left: 2rem">
                  Contacto Demo
                </h3>

                <ul style="font-size: 15px;  margin: 10px 0; line-height: 1.8;">                                                   
                  <li><u>Nombre:</u> <b>${name}</b></li>
                  <li><u>Mail:</u> <b>${email}</b></li>
                  <li><u>Teléfono:</u> <b>${phone}</b></li>
                  <li><u>Empresa:</u> <b>${company}</b></li>
                  <li><u>Dónde nos conoció:</u> <b>${meetUs}</b></li>
                  <li><u>Tema o servicio de interés:</u> <b>${topic}</b></li>
                  <li><u>Mensaje:</u> <b>${message}</b></li>
                </ul>
              </div>
            </td>
          </tr>
        </table>
      </html>
    `
  }

  // Demo user thank you for contact template
  const getEmailDemoUserTemplate = name => {
    
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding-top: 2rem;">
              <img width="15%"" src="${LOGO_CORREO}" alt="Habit Logo">
            </td>
          </tr>
          
          <tr>
            <td style="background-color: #ecf0f1; padding: 1rem">
              <div style="color: #34495e; margin: 2rem; font-family: sans-serif">
                <h2 style="color: #4e8769; margin: 0 0 7px; text-align: center;">
                  ${i18n.t('message.hello')}${name ? ` ${name}` : ''},
                </h2>

                <p style="margin: 2px; font-size: 15px; text-align: center">
                  ${i18n.t('message.thankMessageForContact')}
                </p>

                <div style="width: 100%; text-align: center; margin: 7% 0%;">
                  <span style="color: #4e8769; display: block;">
                    ${i18n.t('message.habitTeam')}
                  </span>
                  <a style="margin-top: 1rem; font-size: 11px; display: block; color: #56616d" href="https://habit-demo.addval.io/login">
                    ${i18n.t('message.visitTheSite')}
                  </a>
                </div>
              </div>
            </td>
          </tr>
        </table>
      </html>
    `
  }

  const sendEmailInstance = async (eventData, id, state) => {
    try {
      const query = { _id: oid(id) }

      const pipeline = [
        { $match: query },
        { $lookup: { from: 'worker', localField: 'participants', foreignField: '_id', as: 'participants', pipeline: [ { $project: { name: 1, email: 1 } } ] } },
        { $project: { participants: 1 }}
      ]

      const items = await getItemsAggregate({ collection: 'event', pipeline })
      if (!items?.[0]) throw new Error('Item not found')

      const info = items[0]

      const emailPromises = info.participants?.map((p) =>  {
        if (p.email) {
          const subject =
            state === 'add' ? i18n.t('message.instance_assigned')
            : state === 'update' ? i18n.t('message.instance_updated')
            : i18n.t('message.instance_deleted')
          let bodyData = {
            name: p.name,
            start: formatEmailDate(eventData.start),                
            participants: info.participants.map(p => p.name).join(" / "),
            creator: userData.username,
            commitment: eventData.extendedProps.improvements.length,
            state,
            id
          }
          eventData.extendedProps.metadata?.forEach(e => {
            Object.assign(bodyData, {[e.name]: e.answer})
          })
          const body = getEmailIntanceTemplate(bodyData)
          return new Promise((resolve, reject) => {
            sendEmail({ to: [p.email], subject, htmlBody: body })
              .then(res => res.MessageId && resolve(res.MessageId))
              .catch(err => console.log(err))
          })                
        }
      })

      const emailResponses = await Promise.all(emailPromises)

      const cantResponses = emailResponses.filter(res => !!res)

      if (cantResponses.length > 0) showSuccessMessage(`${cantResponses.length} ${i18n.t('message.email_send_instance_success')}`)
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.email_send_instance_error'))
    }
  }

  const sendEmailImprovementsOrCommitments = async (improvements, state, improvementsToUpdate, improvementsToDelete, isCommitment = false) => {
    const emailPromises = []
    for (const i of improvements) {  
      // If improvement is not new or flagged for update/delete or assigne doesn/t have email then continue (don't send email)
      if (!state && !i.isNew && !improvementsToUpdate?.find(e => e._id === i._id) && !improvementsToDelete?.find(e => e._id === i._id)) {
        continue
      }

      let recipients = []

      if (i.assignee?.email) recipients.push({ name: i.assignee.name, email: i.assignee.email, category: "assignee" })
      
      if (i.subscribers?.length) {
        try {
          const subscribersData = i.subscribers[0]?._id ? i.subscribers : await getWorkersEmails(i.subscribers)
          recipients = [...recipients, ...subscribersData]
        } catch (error) {
          console.log(error)
        }
      }

      if (!recipients.length) continue

      const updatedState = state
          ? state
          : i.isNew
            ? "add"
            : improvementsToDelete?.find(e => e._id === i._id)
              ? "delete"
              : "update"
  
        let subject
        switch (updatedState) {
          case "update":
            subject = commitmentFunctionality || isCommitment ? i18n.t('message.commitment_updated') : i18n.t('message.improvement_opportunity_updated')
            break;
          case "complete":
            subject = commitmentFunctionality || isCommitment ? i18n.t('message.commitment_completed') : i18n.t('message.improvement_opportunity_completed')
            break;
          case "delete":
            subject = commitmentFunctionality || isCommitment ? i18n.t('message.commitment_deleted') : i18n.t('message.improvement_opportunity_deleted')
            break;
          default:
            subject = commitmentFunctionality || isCommitment ? i18n.t('message.commitment_assigned') : i18n.t('message.improvement_opportunity_assigned')
            break;
        }

        const bodyData = {
          title: isCommitment ? i.title : i.note,
          assignee: i.assignee?.name,
          tags: i.tags?.map(e => i18n.t(`domain.${e}`)).join(" / "),
          creator: userData.username,
          dueDate: formatEmailDate(i.dueDate),
          description: i.description?.replace(/<[^>]*>/g, ''),
          commitmentFunctionality,
          updatedState,
          completed: i.completed,
          deleted: i.deleted,
          id: i._id
        }
        if (i.metadata?.length) {
          i.metadata.forEach(e => {
            if (e.name === "creation_date") e.answer = formatEmailDate(e.answer)
            Object.assign(bodyData, {[e.name]: e.answer})
          })
        }
        if(isCommitment && i.creationDate) {
          bodyData.creationDate = formatEmailDate(i.creationDate)
        } else if (i.creation_date) {
          i.creation_date = formatEmailDate(i.creation_date)
        }

      recipients.forEach(r => {
        if (r.email) {
          bodyData.name = r.name
          bodyData.category = r.category
  
          const body = !isCommitment ? getEmailImprovementTemplate(bodyData) : getEmailCommitmentTemplate(bodyData)
    
          const emailPromise = new Promise((resolve, reject) => {
            sendEmail({ to: [r.email], subject, htmlBody: body })
              .then(res => {
                res.MessageId && resolve(res.MessageId)
              })
              .catch(err => console.log(err))
          })
    
          emailPromises.push(emailPromise)
        }
      })
    }

    return Promise.all(emailPromises)
      .then(emailResponses => {
        const cantResponses = emailResponses.filter(res => !!res)
        if (cantResponses.length > 0) showSuccessMessage(`${cantResponses.length} ${commitmentFunctionality || isCommitment ? i18n.t('message.email_send_commitment_success') : i18n.t('message.email_send_improvement_success')}`) 
      })
      .catch((err) => {
        console.log(err)
        showErrorMessage(commitmentFunctionality || isCommitment ? i18n.t('message.email_send_commitment_error') : i18n.t('message.email_send_improvement_error'))
      })
  }

  const formatEmailDate = (date) => {
    if (!date) return ''

    const dateToFormat = date instanceof Date ? date : new Date(`${date.slice(0, 10)} 12:00:00`)
    return dateToFormat.toLocaleDateString(`${default_language || 'en'}-US`)
  }

  const addOriginIdEvent = async eventId => {
    try {
      const query = { _id: oid(eventId) }
      const item = await getItem({ collection: 'event', query, options: { projection: { improvements: 1 } } })
      if (!item) throw new Error('Item not found')

      const improvementsToUpdate = item.improvements || []

      if (improvementsToUpdate?.length) {
        const updateQuery = { _id: { $in: improvementsToUpdate } }
        const action = { $set: { origin_id: oid(eventId), origin: "event" } }
        await updateItems({ collection: 'improvement', query: updateQuery, action })
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.improvement_update_error'))
    }
  }

  const getWorkersEmails = async (workerIds) => {
    const query = { _id: { $in: workerIds.map(e => oid(e)) } }
    return await getItems({ collection: 'worker', query, options: { projection : { name: 1, email: 1 } } })
  }

  const setImprovementColor = (dueDate, isCompleted) => {
    if (isCompleted) return "primary"
    const dueDateFormatted = dueDate ? new Date(`${dueDate.slice(0, 10)} 12:00:00`) : null
    if (dueDateFormatted < now) return "danger"
    return "warning"
  }

  const createCommitments = async (commitments, improvementId) => {
    if (!commitments?.length) return

    const commitmentCreationPromises = commitments.map(c => {
      const newCommitment = {
        client_id: oid(c.client_id),
        assignee: c.assignee ? oid(c.assignee) : null,
        title: c.title,
        dueDate: c.dueDate ? date(new Date(`${c.dueDate.slice(0, 10)} 12:00:00`)) : null,
        creationDate: c.creationDate ? date(new Date(`${c.creationDate.slice(0, 10)} 12:00:00`)) : null,
        created_by: oid(userId)
      }

      if (improvementId) {
        newCommitment.improvement = oid(improvementId)
      }

      return new Promise(async (resolve, reject) => {
        try {    
          const { insertedId } = await createItem({ collection: 'commitment', payload: newCommitment })
          createCommitmentEvent(newCommitment, insertedId)
          const newCommitmentId = insertedId.toString()
          c._id = newCommitmentId
          c.isNew = true

          // Fetch assignee data for email sending
          try {
            const assigneeData = await getWorkersEmails([newCommitment.assignee])
            if (!assigneeData?.[0]) throw new Error('Assignee not found')
            c.assignee = assigneeData[0]
          } catch (error) {
            console.log(error)
          }
        
          resolve(c)
        } catch (error) {
          console.log(error)
          showErrorMessage(i18n.t('message.commitment_error'))
          resolve(null)
        }
      })
    })

    const resolvedPromises = await Promise.all(commitmentCreationPromises)

    const filteredCommitments = resolvedPromises.filter(e => !!e)

    // Send email to assignee and subscribers with the details of the improvements created
    if (filteredCommitments?.length && sendEmail) {
      sendEmailImprovementsOrCommitments(filteredCommitments, 'add', [], [], true)
    }

    return filteredCommitments.map(e => e._id)
  }

  const createCommitmentEvent = async (commitment, newCommitmentId) => {

    const state = commitment.dueDate && new Date(commitment.dueDate) < new Date()
        ? 'Vencidas'
        : 'Pendientes'

    const endDate = new Date(commitment.dueDate)
    endDate.setHours(13, 0, 0, 0)

    const payload = {
      client_id: commitment.client_id,
      attendee: commitment.assignee,
      commitment: oid(newCommitmentId),
      title: commitment.title,
      start: date(commitment.dueDate),
      end: date(endDate),
      extendedProps: { calendar: state },
      isBehaviourEvaluation: true
    }

    try {    
      await createItem({ collection: 'event', payload })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.event_create_error'))
    }
  }

  const getEmailBarometerSurveyTemplate = bodyData => {
    const { name, surveyName, locationName, link } = bodyData;
    return `
      <html>
        <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
          <tr>
            <td style="background-color: #ecf0f1; text-align: center; padding: 0">
              <img width="18%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="Habit Logo">
            </td>
          </tr>
          
          <tr>
            <h3>${i18n.t('message.hello')}${name ? ` ${name}` : ''},</h3>
            <p>${i18n.t('message.newSurveyCreated')}: <strong>${surveyName}</strong></p>
            <p>${i18n.t('label.Location')}: <strong>${locationName}</strong></p>
            <p>${i18n.t('message.surveyAccessLink')}:</p>
            <p><a href="${link}" target="_blank">${link}</a></p>
            <br>
            <p>${i18n.t('message.thankYouParticipation')}.</p>
          </tr>
        </table>
      </html>
    `
  }

  const uploadFileToS3 = async (fileInfo, destinationFolder) => {
    try {
      const { uploadUrl, key } = await getPresignedUploadUrl({ fileName: fileInfo.name, fileType: fileInfo.type, destinationFolder })

      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': fileInfo.type },
        body: fileInfo.file || fileInfo.base64 || fileInfo,
      })

      return key
    } catch (error) {
      console.log(error)
      throw new Error(error)
    }
  }

  return {
    resolveTagVariant,
    resolveAvatarVariant,
    updateImprovementWithKey,
    getEmailImprovementTemplate,
    addTask,
    sendEmailInstance,
    sendEmailImprovementsOrCommitments,
    addOriginIdEvent,
    getWorkersEmails,
    getEmailDemoHabitTemplate,
    getEmailDemoUserTemplate,
    formatEmailDate,
    setImprovementColor,
    createCommitments,
    createCommitmentEvent,
    getEmailBarometerSurveyTemplate,
    getErrorTemplate,
    uploadFileToS3,
  }
}